'use client';

import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import { Box, IconButton, Typography } from '@mui/material';

import { LiveBadge } from '@/components';
import { formatMarketResolutionDate, formatVolume } from '@/utils/formatters';

interface MarketCardFooterProps {
  volume: number;
  isBookmarked: boolean;
  onBookmarkClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  isResolved?: boolean;
  endDate?: string;
  isLive?: boolean;
  alwaysShowBookmark?: boolean;
}

export function MarketCardFooter({
  volume,
  isBookmarked,
  onBookmarkClick,
  isResolved,
  endDate,
  isLive,
  alwaysShowBookmark = false,
}: MarketCardFooterProps) {
  const formatedVol = formatVolume(volume);
  const formattedEndDate = formatMarketResolutionDate(endDate);

  return (
    <Box
      sx={{
        // Design footer: 13px top border/pad, mono muted "Vol." on the left,
        // LIVE badge inline, bookmark on the right. (Removed the old decorative
        // refresh icon — it had no handler, so nothing functional is lost.)
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mt: 'auto',
        pt: '5px',
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {isLive && <LiveBadge />}
        <Typography
          sx={{
            fontFamily: 'var(--font-plex-mono), IBM Plex Mono, monospace',
            fontSize: '12px',
            color: 'text.secondary',
            fontWeight: 400,
          }}
        >
          {`${formatedVol} Vol.`}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {isResolved && endDate && (
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.875rem',
              color: 'text.secondary',
              fontWeight: 500,
            }}
          >
            {formattedEndDate}
          </Typography>
        )}

        {(!isResolved || alwaysShowBookmark) && (
          <IconButton
            size="small"
            onClick={onBookmarkClick}
            sx={{
              color: isBookmarked ? 'primary.main' : 'text.secondary',
              p: 0.5,
              transition: 'color 0.2s ease',
              '&:hover': {
                color: isBookmarked ? 'primary.dark' : 'text.primary',
              },
            }}
            aria-label="Bookmark"
          >
            {isBookmarked ? (
              <BookmarkIcon sx={{ fontSize: '1.25rem' }} />
            ) : (
              <BookmarkBorderIcon sx={{ fontSize: '1.25rem' }} />
            )}
          </IconButton>
        )}
      </Box>
    </Box>
  );
}
